import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  query,
  where,
  orderBy,
  limit
} from "firebase/firestore";
import Layout from "../components/Layout";
import Papa from "papaparse";

function HawkerCDMManager() {
  const [ledgers, setLedgers] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [csvPreview, setCsvPreview] = useState([]);
  const [selectedPartyId, setSelectedPartyId] = useState(""); // Kept for other functions like template
  const [searchId, setSearchId] = useState("");
  const [searchDate, setSearchDate] = useState("");

  useEffect(() => {
    loadParties();
  }, []);

  const loadParties = async () => {
    try {
      const partySnap = await getDocs(collection(db, "partyMaster"));
      const partyList = partySnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setParties(partyList);
    } catch (err) {
      console.error(err);
    }
  };

  const loadData = async () => {
    if (!searchId && !searchDate) {
      alert("⚠️ Please select a Party ID or a Date first.");
      return;
    }

    setLoading(true);
    try {
      let q = collection(db, "hawker_cdm");
      let conditions = [];

      if (searchId) {
        conditions.push(where("partyId", "==", searchId.trim()));
      }

      if (searchDate) {
        // Reformat yyyy-mm-dd to dd-mm-yyyy
        const [y, m, d] = searchDate.split("-");
        const formattedDate = `${d}-${m}-${y}`;
        conditions.push(where("date", "==", formattedDate));
      }

      const finalQuery = query(q, ...conditions, orderBy("uploadedAt", "desc"));
      const ledgerSnap = await getDocs(finalQuery);
      const ledgerList = ledgerSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLedgers(ledgerList);
    } catch (err) {
      console.error(err);
      if (err.message.includes("index")) {
        alert("This selection requires a Firestore index. Check browser console for the link.");
      } else {
        alert("Error loading data. Try selecting only one filter first.");
      }
    }
    setLoading(false);
  };

  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        setCsvPreview(result.data);
      },
    });
  };

  const uploadCsvToFirestore = async () => {
    if (csvPreview.length === 0) {
      alert("No data to upload.");
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    // Use current IST date for all uploads
    const todayIST = new Date().toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" }).split('/').join('-'); // dd-mm-yyyy

    for (const row of csvPreview) {
      try {
        const pId = (row.partyId || row.PartyID || "").trim();
        if (!pId) {
          failCount++;
          continue;
        }

        // Enrich from partyMaster
        const partyInfo = parties.find(p => p.partyId === pId);
        
        const payload = {
          date: todayIST, // Automated: uses current date only
          partyId: pId,
          name: partyInfo?.name || row.name || "Unknown",
          partytype: partyInfo?.partytype || "hawker",
          payment_mode: partyInfo?.payment_mode || "Cash",
          clientId: partyInfo?.clientId || "",
          cdm: Number(row.cdm || 0),
          nicheBrands: Number(row.nicheBrands || 0),
          uploadedAt: new Date().toISOString()
        };

        // Unique ID: partyId + date
        const docId = `${pId}_${todayIST}`;
        await setDoc(doc(db, "hawker_cdm", docId), payload, { merge: true });
        successCount++;
      } catch (err) {
        console.error("Error uploading row:", row, err);
        failCount++;
      }
    }

    alert(`Upload complete!\nToday's Date (${todayIST}) applied to ${successCount} records.\nFailed: ${failCount}`);
    loadData();
    setShowBulk(false);
    setCsvPreview([]);
    setLoading(false);
  };

  const downloadTemplate = () => {
    // Filter only hawker parties to assist user
    const hawkerParties = parties.filter(p => p.partytype === 'hawker');
    
    if (hawkerParties.length === 0) {
      alert("No hawker parties found in Party Master. Please add them first.");
      return;
    }

    const headers = "partyId,cdm,nicheBrands";
    const body = hawkerParties.map(p => `${p.partyId},0,0`).join("\n");
    
    const csvContent = headers + "\n" + body;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `hawker_cdm_template_${new Date().toLocaleDateString('en-GB').split('/').join('-')}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <div style={{ padding: "40px 20px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          
          {/* HEADER & TOP ACTIONS */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32, flexWrap: "wrap", gap: 20 }}>
            <div style={{ borderLeft: "6px solid #4f46e5", paddingLeft: 20 }}>
              <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.025em" }}>
                Hawker CDM & Niche Brand Sales
              </h1>
            </div>
            
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={downloadTemplate} style={secondaryActionStyle}>
                📥 Get CSV Template
              </button>
              <button 
                onClick={() => setShowBulk(!showBulk)} 
                style={{ ...primaryActionStyle, background: showBulk ? "#f1f5f9" : "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)", color: showBulk ? "#475569" : "#fff"  }}
              >
                {showBulk ? "✖ Close Upload" : "➕ Upload CDM Data"}
              </button>
            </div>
          </div>

          {/* ===== SEARCH & DATE SELECTOR ===== */}
          <div style={{ background: "#ffffff", padding: "24px", borderRadius: 16, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", marginBottom: 32, display: "flex", gap: 20, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: 2, minWidth: "250px" }}>
              <label style={labelStyle}>PARTY IDENTIFIER</label>
              <input 
                type="text" 
                placeholder="Ex: HAWK001..." 
                value={searchId}
                onChange={e => setSearchId(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div style={{ flex: 1, minWidth: "180px" }}>
              <label style={labelStyle}>COLLECTION DATE</label>
              <input 
                type="date" 
                value={searchDate} 
                onChange={e => setSearchDate(e.target.value)} 
                style={inputStyle}
              />
            </div>

            <button 
              onClick={loadData}
              disabled={loading}
              style={{ ...primaryActionStyle, padding: "14px 40px" }}
            >
              {loading ? "SEARCHING..." : "🔍 FETCH DATA"}
            </button>
          </div>

        {showBulk && (
          <div style={{ background: "#ffffff", padding: 32, borderRadius: 16, border: "1px solid #4f46e5", marginBottom: 32, boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }}>
            <h3 style={{ marginTop: 0, fontSize: "12px", letterSpacing: "0.1em", color: "#4f46e5", textTransform: "uppercase", fontWeight: 800 }}>BULK DATA UPLOAD</h3>
            <p style={{ fontSize: "14px", color: "#64748b", marginBottom: 24, fontWeight: 500 }}>
              The current date will be applied automatically. <br/>
              <b>Column Headers:</b> <code style={{ background: "#f1f5f9", padding: "2px 6px", borderRadius: 4 }}>partyId, cdm, nicheBrands</code>
            </p>
            
            <div style={{ background: "#f8fafc", padding: 20, borderRadius: 12, border: "2px dashed #e2e8f0", textAlign: "center", marginBottom: 24 }}>
              <input type="file" accept=".csv" onChange={handleCsvFile} style={{ fontSize: "14px", color: "#475569" }} />
            </div>
            
            {csvPreview.length > 0 && (
              <div style={{ marginTop: 15 }}>
                <div style={{ maxHeight: "250px", overflow: "auto", border: "1px solid #e2e8f0", background: "#fff", marginBottom: 24, borderRadius: 12, overflowX: "hidden" }}>
                   <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead style={{ background: "#f8fafc", position: "sticky", top: 0 }}>
                        <tr style={{ textAlign: "left" }}>
                          {Object.keys(csvPreview[0]).map(h => <th key={h} style={tableHeaderStyle}>{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.slice(0, 10).map((row, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                            {Object.values(row).map((v, j) => <td key={j} style={{ padding: "12px 20px" }}>{v}</td>)}
                          </tr>
                        ))}
                      </tbody>
                   </table>
                   {csvPreview.length > 10 && <div style={{ fontSize: "11px", padding: "12px", textAlign: "center", color: "#94a3b8", background: "#f8fafc", fontWeight: 600 }}>... {csvPreview.length - 10} more records ready</div>}
                </div>
                <button 
                  onClick={uploadCsvToFirestore} 
                  disabled={loading}
                  style={primaryActionStyle}
                >
                  {loading ? "📦 SYNCING RECORDS..." : `✅ COMMIT ${csvPreview.length} RECORDS FOR TODAY`}
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{ background: "#ffffff", borderRadius: 16, boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <div style={{ overflowX: "auto", maxHeight: "72vh" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={tableHeaderStyle}>DATE</th>
                  <th style={tableHeaderStyle}>PARTY IDENTIFIER</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>CDM AMOUNT</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>NICHE BRANDS</th>
                  <th style={tableHeaderStyle}>TERMINAL ID</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>SYNC TIME</th>
                </tr>
              </thead>
              <tbody>
                {ledgers.map((l, idx) => (
                  <tr key={l.id} style={{ borderBottom: "1px solid #f1f5f9", background: idx % 2 === 0 ? "#ffffff" : "#fafafa" }}>
                    <td style={{ padding: "18px 20px", fontWeight: 700, color: "#1e293b" }}>{l.date}</td>
                    <td style={{ padding: "18px 20px", color: "#4f46e5", fontWeight: 700 }}>{l.partyId}</td>
                    <td style={{ padding: "18px 20px", textAlign: "right", color: "#059669", fontWeight: 800 }}>
                      ₹ {Number(l.cdm || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: "18px 20px", textAlign: "right", color: "#1e293b", fontWeight: 600 }}>
                      ₹ {Number(l.nicheBrands || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: "18px 20px" }}>
                      <span style={{ fontSize: "11px", background: "#f1f5f9", color: "#64748b", padding: "4px 10px", borderRadius: 20, fontWeight: 700, textTransform: "uppercase" }}>
                        {l.clientId || "MDSons"}
                      </span>
                    </td>
                    <td style={{ padding: "18px 20px", textAlign: "right", color: "#94a3b8", fontSize: "13px" }}>
                      {l.uploadedAt ? new Date(l.uploadedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "-"}
                    </td>
                  </tr>
                ))}
                {ledgers.length === 0 && !loading && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center", padding: 80 }}>
                      <div style={{ fontSize: "48px", marginBottom: "16px" }}>📂</div>
                      <h3 style={{ color: "#1e293b", margin: 0 }}>No Records Found</h3>
                      <p style={{ color: "#64748b", marginTop: 8 }}>Select a party or date and click "Fetch Data" to view history.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </Layout>
);
}

const primaryActionStyle = {
  width: "100%",
  padding: "10px 24px",
  fontSize: "14px",
  fontWeight: 700,
  background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
  color: "#ffffff",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.3)",
};

const secondaryActionStyle = {
  ...primaryActionStyle,
  width: "auto",
  background: "#f1f5f9",
  color: "#475569",
  boxShadow: "none",
};

const inputStyle = {
  width: "100%",
  padding: "12px 16px",
  fontSize: "15px",
  borderRadius: "10px",
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#1e293b",
  outline: "none",
  boxSizing: "border-box"
};

const labelStyle = {
  display: "block",
  fontSize: "11px",
  fontWeight: 800,
  color: "#94a3b8",
  marginBottom: "8px",
  letterSpacing: "0.05em",
};

const tableHeaderStyle = {
  padding: "16px 20px",
  fontSize: "11px",
  fontWeight: 800,
  color: "#64748b",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  textAlign: "left",
  borderBottom: "1px solid #e2e8f0"
};

export default HawkerCDMManager;
